/**
 * DQTab — Quality issues with severity filtering and click-to-expand
 * drill-down showing up to 25 sample violating rows per issue.
 *
 * The offending column is highlighted in the sample table (amber header +
 * red-tinted cell background) so users instantly see where the violation
 * lives. An actionable callout above the sample table explains the issue
 * and gives a concrete remediation step.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import type { IssueDetail } from "./DetailPanel";
import type { SeverityLevel } from "../discovery/ScoreHero";

interface DQTabProps {
  issues: IssueDetail[];
}

const SEVERITY_ORDER: SeverityLevel[] = [
  "critical",
  "poor",
  "warning",
  "moderate",
  "good",
  "excellent",
];

const SEVERITY_STYLE: Record<
  SeverityLevel,
  { icon: string; color: string; label: string }
> = {
  critical: {
    icon: "●",
    color: "var(--readiness-critical)",
    label: "Critical",
  },
  poor: { icon: "◆", color: "var(--readiness-poor)", label: "Poor" },
  moderate: {
    icon: "▲",
    color: "var(--readiness-moderate)",
    label: "Moderate",
  },
  warning: { icon: "▲", color: "var(--readiness-warning)", label: "Warning" },
  good: { icon: "✓", color: "var(--readiness-good)", label: "Good" },
  excellent: {
    icon: "■",
    color: "var(--readiness-excellent)",
    label: "Excellent",
  },
};

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

/**
 * Generate a concrete, actionable fix hint based on the issue dimension
 * and message. Much more useful than a generic "fix nulls" instruction.
 */
function tailoredFix(issue: IssueDetail): string {
  const dim = (issue.dimension || "").toLowerCase();
  const msg = (issue.message || "").toLowerCase();
  const col = issue.column || "this column";

  if (
    dim === "completeness" ||
    msg.includes("null") ||
    msg.includes("missing")
  ) {
    const pctMatch = issue.message.match(/(\d+(?:\.\d+)?)\s*%/);
    const pct = pctMatch ? pctMatch[1] : null;
    return pct
      ? `${pct}% of rows are NULL in "${col}". Backfill with a sensible default, then add a NOT NULL constraint. Trace the ETL step that produces these NULLs and add upstream validation.`
      : `"${col}" has missing values. Add a NOT NULL constraint after backfilling. Audit the upstream source or ETL step for rows that skip this field.`;
  }

  if (
    dim === "uniqueness" ||
    msg.includes("duplicate") ||
    msg.includes("unique")
  ) {
    return `"${col}" has duplicate values where uniqueness is expected. Run: SELECT "${col}", COUNT(*) GROUP BY "${col}" HAVING COUNT(*) > 1 to identify duplicates. Add a UNIQUE constraint once deduplication is complete.`;
  }

  if (
    dim === "validity" ||
    msg.includes("format") ||
    msg.includes("pattern") ||
    msg.includes("invalid")
  ) {
    return `"${col}" contains values that fail type/format validation. Add a CHECK constraint or regex-based rule at ingestion. Review sample rows below to identify the malformed pattern.`;
  }

  if (
    dim === "consistency" ||
    msg.includes("referential") ||
    msg.includes("foreign") ||
    msg.includes("mismatch")
  ) {
    return `"${col}" has a referential integrity or consistency violation. Verify the FK relationship and remove or correct orphaned rows. Consider enforcing FK constraints at the database level.`;
  }

  if (
    msg.includes("outlier") ||
    msg.includes("range") ||
    msg.includes("out-of-range")
  ) {
    return `"${col}" has out-of-range values. Query MIN/MAX/AVG/STDDEV to understand the distribution, then add a CHECK constraint for valid bounds.`;
  }

  if (
    msg.includes("cardinality") ||
    msg.includes("distinct") ||
    msg.includes("singleton")
  ) {
    return `"${col}" has unexpectedly low or high cardinality. Verify the column's domain — this may indicate data mapping errors, a stale lookup table, or an enum that has bloated.`;
  }

  return `Investigate "${col}" and remediate at source. Expand the sample rows below to identify the specific violation pattern.`;
}

export function DQTab({ issues }: DQTabProps) {
  const [filter, setFilter] = useState<SeverityLevel | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const filtered = filter
    ? issues.filter((i) => i.severity === filter)
    : issues;
  const sorted = [...filtered].sort(
    (a, b) =>
      SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );

  if (issues.length === 0) {
    return (
      <p className="text-caption text-[var(--muted-foreground)]">
        No quality issues detected.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex gap-1">
        <button
          onClick={() => setFilter(null)}
          className={`px-2 py-1 rounded-md text-caption transition-colors ${
            !filter
              ? "bg-white/10 text-[var(--foreground)]"
              : "text-[var(--muted-foreground)]"
          }`}
        >
          All ({issues.length})
        </button>
        {SEVERITY_ORDER.map((sev) => {
          const count = issues.filter((i) => i.severity === sev).length;
          if (count === 0) return null;
          const { icon, color, label } = SEVERITY_STYLE[sev];
          // The "good"/"excellent" buckets are checks that PASSED, not
          // defects — calling them "issues" reads as an oxymoron.
          const isPassing = sev === "good" || sev === "excellent";
          const noun = isPassing ? "passing check" : "issue";
          return (
            <button
              key={sev}
              onClick={() => setFilter(filter === sev ? null : sev)}
              title={`${label}: ${count} ${noun}${count === 1 ? "" : "s"}`}
              aria-label={`Filter by ${label}: ${count} ${noun}${count === 1 ? "" : "s"}`}
              className={`px-2 py-1 rounded-md text-caption transition-colors ${
                filter === sev
                  ? "ring-1 ring-current"
                  : "opacity-60 hover:opacity-90"
              }`}
              style={{ color }}
            >
              {icon}
              {count}
            </button>
          );
        })}
      </div>

      {/* Issue list with drill-down */}
      <div className="space-y-1">
        {sorted.map((issue, i) => {
          const style = SEVERITY_STYLE[issue.severity];
          const hasSamples =
            Array.isArray(issue.sampleFailures) &&
            issue.sampleFailures.length > 0;
          const isOpen = expandedIdx === i;
          const sampleCols =
            issue.sampleFailureColumns ??
            (issue.sampleFailures && issue.sampleFailures[0]
              ? Object.keys(issue.sampleFailures[0])
              : []);

          // Identify the offending column(s) to highlight in the sample table.
          // `issue.column` may be a comma-separated list; normalise to a Set.
          // Special case: column === "*" means a table-level issue (e.g. overall
          // quality score). In that case every sample column is highlighted.
          const rawOffending = (issue.column || "")
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
          const isTableLevel = rawOffending.includes("*");
          const offendingCols = new Set(rawOffending);

          // EVERY issue is expandable — even without sample rows we still
          // have an actionable fix + context to show. Business-rule
          // violations in particular don't carry `sampleFailures` from the
          // backend, but the user still wants to see remediation guidance.
          return (
            <div key={i} className="rounded-md hover:bg-white/5">
              <button
                type="button"
                onClick={() => setExpandedIdx(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="w-full flex items-start gap-2 px-2 py-1.5 text-left cursor-pointer"
              >
                {isOpen ? (
                  <ChevronDown className="w-3 h-3 shrink-0 mt-1 text-white/45" />
                ) : (
                  <ChevronRight className="w-3 h-3 shrink-0 mt-1 text-white/45" />
                )}
                <span
                  className="text-caption shrink-0 mt-0.5"
                  style={{ color: style.color }}
                  title={style.label}
                >
                  {style.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-caption text-[var(--foreground)]">
                    {issue.message}
                  </p>
                  <p className="text-caption text-[var(--muted-foreground)]">
                    {issue.column}
                    {issue.dimension ? ` · ${issue.dimension}` : ""}
                    {hasSamples
                      ? ` · ${issue.sampleFailures!.length} sample${
                          issue.sampleFailures!.length === 1 ? "" : "s"
                        }`
                      : ""}
                  </p>
                </div>
              </button>

              {isOpen && (
                <div className="mx-2 mb-2 mt-1 space-y-2">
                  {/* Actionable fix callout — always shown */}
                  <div className="flex items-start gap-2 rounded-md bg-amber-400/8 border border-amber-400/20 px-3 py-2">
                    <Wrench className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-100/85 leading-snug">
                      {tailoredFix(issue)}
                    </p>
                  </div>

                  {/* When no sample rows were captured (e.g. business-rule
                      violations don't include them), fall back to a compact
                      context panel so the drill-down is never empty. */}
                  {!hasSamples && (
                    <div className="rounded-md bg-black/30 border border-white/10 px-3 py-2 space-y-1">
                      <div className="text-[11px] text-white/60">
                        <span className="text-white/40">Dimension:</span>{" "}
                        <span className="font-mono text-white/80">
                          {issue.dimension || "—"}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/60">
                        <span className="text-white/40">Column:</span>{" "}
                        <span className="font-mono text-white/80">
                          {issue.column || "table-level"}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/60">
                        <span className="text-white/40">Details:</span>{" "}
                        <span className="text-white/80">{issue.message}</span>
                      </div>
                      <p className="text-[11px] text-white/40 italic pt-1">
                        No sample rows were captured for this finding —
                        business-rule violations typically report counts rather
                        than individual rows.
                      </p>
                    </div>
                  )}

                  {/* Sample rows — offending column highlighted */}
                  {hasSamples && (
                    <div
                      className="rounded-md bg-black/40 border border-white/10 overflow-auto max-h-64"
                      role="region"
                      aria-label="Sample violating rows"
                    >
                      <table className="w-full text-[11px]">
                        <thead className="sticky top-0 bg-black/60 backdrop-blur">
                          <tr>
                            {sampleCols.map((c) => {
                              const isOffHdr =
                                isTableLevel || offendingCols.has(c);
                              return (
                                <th
                                  key={c}
                                  className={`text-left px-2 py-1 font-mono font-normal border-b border-white/10 ${
                                    isOffHdr
                                      ? "text-amber-300 bg-amber-400/10"
                                      : "text-white/60"
                                  }`}
                                >
                                  {isOffHdr ? `⚠ ${c}` : c}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {issue.sampleFailures!.map((row, r) => (
                            <tr
                              key={r}
                              className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                            >
                              {sampleCols.map((c) => {
                                const val = fmtValue(row[c]);
                                const isOffending =
                                  isTableLevel || offendingCols.has(c);
                                const isEmpty = val === "—";
                                return (
                                  <td
                                    key={c}
                                    className={`px-2 py-1 font-mono whitespace-nowrap ${
                                      isOffending
                                        ? isEmpty
                                          ? "text-red-300 bg-red-400/10 font-semibold"
                                          : "text-amber-200 bg-amber-400/8"
                                        : "text-white/80"
                                    }`}
                                  >
                                    {val}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
